/**
 * AiAssistantDock - Context-Aware AI Assistant Panel
 *
 * Dockable, collapsible, and resizable AI assistant panel.
 * Adapts to the current workspace context.
 *
 * Rewritten 2026-09-23: previously a fully separate, stateless implementation
 * that called LlmService.sendToLlmPlayground directly -- no sessionId, no
 * chat_session/chat_message persistence, no agent orchestration or
 * tool-calling, and its own hand-rolled message bubble (no table/chart/
 * action rendering). A browser refresh silently lost the entire
 * conversation because nothing was ever saved server-side to lose.
 *
 * Now reuses the SAME real chat pipeline and rendering component as the main
 * Atom chat page (AtomApp.js): MessageRenderer (table/chart/action rendering,
 * markdown, everything) and ChatService (the real /api/chat + session
 * endpoints, going through ChatProcessingService/agent orchestration like
 * any other Atom conversation) -- but with its OWN session, independent of
 * the user's main session list, so a dock conversation and a full-page Atom
 * conversation never mix. The session id is persisted client-side
 * (localStorage) rather than server-tagged, since this dock only ever needs
 * ONE ongoing conversation (no session switcher/history UI here) -- no
 * backend changes needed.
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Tooltip,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
  Clear as ClearIcon,
  Lightbulb as SuggestionIcon,
} from '@mui/icons-material';
import AtomIcon from '../common/AtomIcon';
import MessageRenderer from '../MessageRenderer';
import { detectResponseType, getResponseText } from '../../utils/sampleResponses';
import { useAuth } from '../../contexts/AuthContext';

import { AI_CONTEXTS } from './constants';
import ChatService from '../../services/ChatService';
import ChatTurnActivity from '../ChatTurnActivity';

const DOCK_SESSION_STORAGE_KEY = 'fast_studio_dock_session_id';

/**
 * Suggestion Chip
 */
const SuggestionChip = ({ label, onClick, colors }) => (
  <Chip
    label={label}
    size="small"
    onClick={onClick}
    sx={{
      height: 24,
      fontSize: '0.7rem',
      bgcolor: colors.surfaceElevated,
      color: colors.textSecondary,
      border: `1px solid ${colors.border}`,
      borderRadius: 0,
      cursor: 'pointer',
      '&:hover': {
        bgcolor: colors.surfaceHover,
        borderColor: colors.accent,
        color: colors.accent,
      },
    }}
  />
);

/** Converts a getSessionMessages() row into MessageRenderer-shaped entries. */
// GET /api/sessions/{sessionId}/messages returns one row PER real message
// ({role: "user"|"assistant"|"system", content, timestamp} -- see
// ChatController.getSessionMessages), not a paired {userMessage, botResponse}
// turn -- this previously assumed the wrong shape, so every restored bubble's
// text was undefined (same real bug fixed the same day in AtomApp.js's and
// useSessionManagement.js's own loadSessionMessages).
// Note: historical messages only have the plain response text, not the
// original structured ChatResponse (table/chart/actions) -- that's a
// pre-existing limitation of getSessionMessages/chat_message storage.
const formatHistoryMessage = (msg, index) => ({
  id: `${msg.timestamp}-${index}`,
  text: msg.content,
  sender: msg.role === 'user' ? 'user' : 'bot',
  timestamp: new Date(msg.timestamp).toLocaleTimeString(),
});

/**
 * AiAssistantDock Component
 */
const AiAssistantDock = ({
  colors,
  open,
  width,
  onToggle,
  onResize,
  context,
  workspaceName,
}) => {
  const { user, selectedConnectedSystem } = useAuth();

  // Messages state
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // This dock's own session id -- independent of AtomApp's session list.
  const [sessionId, setSessionId] = useState(null);
  const [isRestoringSession, setIsRestoringSession] = useState(true);

  // Refs
  const messagesEndRef = useRef(null);
  const resizeRef = useRef(null);

  // Get context config
  const contextConfig = AI_CONTEXTS[context] || AI_CONTEXTS.general;

  // MessageRenderer (and its sub-renderers -- TableRenderer, ChartRenderer,
  // etc.) were written against a FLAT color shape (colors.textPrimary,
  // colors.accent, ...), but ThemeContext exposes nested groups (text.primary,
  // primary.main, ...) -- the same mismatch already found and fixed in
  // FlexWorkspace.js. The dock's own pre-existing chrome (header, resize
  // handle, badges below) already uses flat keys directly against this same
  // raw `colors` prop, so left untouched here; this normalized version is
  // only for the new MessageRenderer usage so real-data table/chart content
  // actually gets real colors instead of silently-invalid CSS.
  const messageColors = useMemo(() => {
    const c = colors || {};
    const pick = (flatKey, nested, fallback) =>
      typeof c[flatKey] === 'string' ? c[flatKey] : (nested || fallback);
    return {
      ...c,
      accent: pick('accent', c.primary?.main, '#3b82f6'),
      textPrimary: pick('textPrimary', c.text?.primary, '#f4f4f5'),
      textSecondary: pick('textSecondary', c.text?.secondary, '#d4d4d8'),
      textMuted: pick('textMuted', c.text?.muted, '#a1a1aa'),
      border: pick('border', c.border?.default, '#3f3f46'),
      warning: pick('warning', c.warning?.main, '#f59e0b'),
      info: pick('info', c.info?.main, '#3b82f6'),
      success: pick('success', c.success?.main, '#22c55e'),
      error: pick('error', c.error?.main, '#ef4444'),
    };
  }, [colors]);

  // Warn before an accidental refresh/close while Atom is still generating a
  // response -- same pattern as AtomApp.js. Cannot actually block a refresh
  // (browsers forbid that), only offer the native confirm dialog.
  useEffect(() => {
    if (!isLoading) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isLoading]);

  // Restore this dock's own session (if one exists) on mount -- same
  // rehydration call AtomApp's loadSessionMessages makes, just scoped to a
  // sessionId this dock persisted itself rather than the user's main
  // session list.
  useEffect(() => {
    const restoreSession = async () => {
      const savedSessionId = localStorage.getItem(DOCK_SESSION_STORAGE_KEY);
      if (!savedSessionId) {
        setIsRestoringSession(false);
        return;
      }
      try {
        const messagesData = await ChatService.getSessionMessages(savedSessionId);
        if (messagesData && messagesData.length > 0) {
          setSessionId(savedSessionId);
          setMessages(messagesData.filter(msg => msg.role !== 'system').map(formatHistoryMessage));
        } else {
          // Session id saved but nothing there anymore (e.g. deleted/expired
          // server-side) -- don't keep pointing at a dead session.
          localStorage.removeItem(DOCK_SESSION_STORAGE_KEY);
        }
      } catch (err) {
        console.warn('AiAssistantDock: failed to restore session, starting fresh', err);
        localStorage.removeItem(DOCK_SESSION_STORAGE_KEY);
      } finally {
        setIsRestoringSession(false);
      }
    };
    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Context-specific suggestions
  const getSuggestions = () => {
    switch (context) {
      case 'llm':
        return [
          'Help me write a prompt for...',
          'How do I use variables?',
          'Optimize this prompt',
        ];
      case 'flow':
        return [
          'Generate a script to...',
          'Explain FLEX syntax',
          'Debug my script',
        ];
      case 'api':
        return [
          'What headers do I need?',
          'Parse this JSON response',
          'Debug 401 error',
        ];
      case 'registry':
        return [
          'Explain this entity',
          'How do I configure...',
          'Best practices for...',
        ];
      default:
        return [
          'What can you help with?',
          'Getting started guide',
          'Explain FAST Studio',
        ];
    }
  };

  // Scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  /** Shared send path for both the input box and action-button clicks. */
  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: trimmed,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError('');

    try {
      // Backend's ChatRequest.workspaceContext is a plain String (a workspace
      // code, e.g. "INVENTORY", used directly for agent-prompt scoping in
      // PromptSubstitutionService) -- sending a nested {source, context,
      // workspaceName} object here caused a real, reproducible crash: Jackson
      // threw "Cannot deserialize value of type java.lang.String from Object
      // value" on every single message sent from this dock (confirmed live
      // 2026-09-23, e.g. asking "what do I do here?" from the Registry
      // workspace). Send just the workspace name/code the backend expects.
      const response = await ChatService.sendMessage(
        trimmed,
        user?.id,
        sessionId,
        selectedConnectedSystem?.id,
        {
          workspaceContext: workspaceName || context || 'general',
        }
      );

      // First message of a brand-new dock conversation -- persist the
      // server-generated sessionId so a refresh restores this same thread.
      if (response.sessionId && !sessionId) {
        setSessionId(response.sessionId);
        localStorage.setItem(DOCK_SESSION_STORAGE_KEY, response.sessionId);
      }

      const responseType = detectResponseType(response);
      const displayText = getResponseText(response);

      const botMessage = {
        id: Date.now() + 1,
        text: displayText || response.response || 'Response received',
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString(),
        responseData: response,
        responseType,
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (err) {
      const errorMessage = {
        id: Date.now() + 1,
        text: `Sorry, I encountered an error: ${err.message || 'Unknown error'}. Please try again.`,
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString(),
      };
      setMessages(prev => [...prev, errorMessage]);
      setError(err.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
      setTimeout(scrollToBottom, 100);
    }
  }, [isLoading, user?.id, sessionId, selectedConnectedSystem?.id, context, workspaceName, scrollToBottom]);

  // Handle send from the input box
  const handleSend = useCallback(() => {
    if (!inputValue.trim() || isLoading) return;
    const text = inputValue.trim();
    setInputValue('');
    sendMessage(text);
  }, [inputValue, isLoading, sendMessage]);

  // Handle a table/action-button click ({"actions":[...]} etc.) -- same
  // "resubmit the sentence as a new user turn" mechanic AtomApp's
  // handleActionClick uses for the common case. AtomApp-specific sentinels
  // (__OPEN_WORKSPACE__, __DIRECT_TOOL__, etc.) aren't handled here -- the
  // dock has no workspace-tab concept of its own to route them to; an
  // agent's response containing one of those will just resend its literal
  // sentence text like any other action for now.
  const handleActionClick = useCallback((sentence) => {
    if (!sentence || !sentence.trim()) {
      return;
    }
    sendMessage(sentence);
  }, [sendMessage]);

  // Handle suggestion click
  const handleSuggestionClick = useCallback((suggestion) => {
    setInputValue(suggestion);
  }, []);

  // Handle clear -- starts a genuinely new session rather than just wiping
  // the visible list, so the next message doesn't silently resume the old
  // (now-hidden) conversation's context server-side.
  const handleClear = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem(DOCK_SESSION_STORAGE_KEY);
  }, []);

  // Handle key press
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // Handle resize drag
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;

    const handleMouseMove = (e) => {
      const delta = startX - e.clientX;
      onResize(startWidth + delta);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [width, onResize]);

  // Collapsed state is now handled by FastStudioShell edge toggle
  if (!open) return null;

  return (
    <Box
      sx={{
        width,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.surface,
        borderLeft: `1px solid ${colors.border}`,
        position: 'relative',
        minHeight: 0,
      }}
    >
      {/* Resize Handle */}
      <Box
        ref={resizeRef}
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          cursor: 'col-resize',
          '&:hover': { bgcolor: colors.accent + '40' },
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AtomIcon sx={{ fontSize: 18 }} />
          <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
            Atom (AI Assistant)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Clear conversation">
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{ color: colors.textMuted, '&:hover': { color: colors.error } }}
            >
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Context Badge */}
      <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${colors.border}` }}>
        <Chip
          icon={<SuggestionIcon sx={{ fontSize: 14 }} />}
          label={contextConfig.name}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.7rem',
            bgcolor: colors.accent + '20',
            color: colors.accent,
            '& .MuiChip-icon': { color: colors.accent },
          }}
        />
      </Box>

      {/* Messages */}
      {/* minHeight: 0 overrides the flex-item default (min-height: auto), which otherwise
          lets this box grow to fit all message content instead of clipping to the
          available space — without it, overflow:'auto' never actually engages here, so
          no scrollbar renders and an ancestor silently absorbs the overflow instead. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          p: 2,
        }}
      >
        {isRestoringSession ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} sx={{ color: colors.accent }} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <AtomIcon sx={{ fontSize: 48, opacity: 0.6, mb: 2 }} />
            <Typography variant="body2" sx={{ color: colors.textMuted, mb: 2 }}>
              How can I help you?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
              {getSuggestions().map((suggestion, index) => (
                <SuggestionChip
                  key={index}
                  label={suggestion}
                  onClick={() => handleSuggestionClick(suggestion)}
                  colors={colors}
                />
              ))}
            </Box>
          </Box>
        ) : (
          messages.map(message => {
            const isUser = message.sender === 'user';
            return (
              <Box
                key={message.id}
                sx={{
                  mb: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isUser ? 'flex-end' : 'flex-start',
                }}
              >
                <Box
                  sx={{
                    maxWidth: '90%',
                    minWidth: 0,
                    overflow: 'hidden',
                    p: 1.5,
                    bgcolor: isUser ? messageColors.accent + '20' : messageColors.surface,
                    border: `1px solid ${isUser ? messageColors.accent + '40' : messageColors.border}`,
                    borderLeft: !isUser ? '3px solid #22c55e' : undefined,
                    borderRadius: 0,
                  }}
                >
                  {isUser ? (
                    <Typography variant="body2" sx={{ color: messageColors.textPrimary, whiteSpace: 'pre-wrap' }}>
                      {message.text}
                    </Typography>
                  ) : (
                    // MessageRenderer only handles the bot side (markdown, tables,
                    // charts, actions) -- matches AtomApp.js's own pattern, which
                    // wraps it in this same kind of bubble rather than calling it
                    // for user messages too (message.responseData is undefined for
                    // a plain user turn, so MessageRenderer would just echo the
                    // text back with none of this bubble's styling if reused here).
                    <MessageRenderer
                      message={message}
                      colors={messageColors}
                      isDark={false}
                      onActionClick={handleActionClick}
                    />
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: messageColors.textMuted, mt: 0.5, fontSize: '0.65rem' }}>
                  {message.timestamp}
                </Typography>
              </Box>
            );
          })
        )}
        {isLoading && (
          <Box sx={{ mb: 2 }}>
            <ChatTurnActivity colors={{ textPrimary: colors.textPrimary, textSecondary: colors.textMuted }} accent={colors.accent} compact />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          p: 1.5,
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        {error && (
          <Typography variant="caption" sx={{ color: colors.error, display: 'block', mb: 0.5 }}>
            {error}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Ask anything..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                bgcolor: colors.surfaceElevated,
                fontSize: '0.85rem',
                '& fieldset': { borderColor: colors.border },
                '&:hover fieldset': { borderColor: colors.accent },
                '&.Mui-focused fieldset': { borderColor: colors.accent },
              },
              '& .MuiInputBase-input': { color: colors.textPrimary, py: 1 },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            sx={{
              bgcolor: colors.accent,
              color: '#fff',
              borderRadius: 0,
              '&:hover': { bgcolor: colors.accentDark },
              '&.Mui-disabled': { bgcolor: colors.surfaceElevated, color: colors.textMuted },
            }}
          >
            <SendIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default AiAssistantDock;
